import React, { useState } from 'react';
import useApi from '../../hooks/useApi';
import { ManagedUser, Role } from '../../types';
import styles from './InviteUserModal.module.css';

interface InviteUserModalProps {
  roles: Role[];
  onClose: () => void;
  onUserInvited: (user: ManagedUser) => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ roles, onClose, onUserInvited }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [partnerGroup, setPartnerGroup] = useState('');
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const { securedRequest, loading } = useApi();

  const filteredRoles = roles.filter(role => role.name !== 'LocaliQ-Partner-Admin');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const { data, error } = await securedRequest('post', '/api/admin/users', {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      partnerGroup: partnerGroup.trim(),
      roles: selectedRole ? [selectedRole] : [],
    });

    if (error) {
      const message = typeof error === 'object' && error.error ? error.error : 'Failed to invite user.';
      setFormError(message);
      return;
    }

    // Ensure roleName is set even if the backend didn't return it
    if (!data.roleName && selectedRole) {
      const matchedRole = roles.find(r => r.id === selectedRole);
      if (matchedRole) {
        data.roleName = matchedRole.name;
      }
    }

    onUserInvited(data);
    onClose();
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} card`}>
        <div className="card-body">
          <h5 className="card-title">Invite User</h5>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="inviteEmail" className="form-label">Email address</label>
              <input
                id="inviteEmail"
                type="email"
                className="form-control"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="inviteFirstName" className="form-label">First Name</label>
              <input
                id="inviteFirstName"
                type="text"
                className="form-control"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="inviteLastName" className="form-label">Last Name</label>
              <input
                id="inviteLastName"
                type="text"
                className="form-control"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="invitePartnerGroup" className="form-label">Partner Group</label>
              <input
                id="invitePartnerGroup"
                type="text"
                className="form-control"
                value={partnerGroup}
                onChange={e => setPartnerGroup(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Roles</label>
              <div className={styles.permissionList}>
                {filteredRoles.length === 0 && (
                  <small className="text-muted">No roles configured.</small>
                )}
                {filteredRoles.map(role => (
                  <div key={role.id} className="form-check">
                    <input
                      id={`invite-role-${role.id}`}
                      type="radio"
                      name="userRole"
                      className="form-check-input"
                      checked={selectedRole === role.id}
                      onChange={() => setSelectedRole(role.id)}
                    />
                    <label htmlFor={`invite-role-${role.id}`} className="form-check-label">
                      {role.name}
                      {role.description && (
                        <small className={`text-muted ms-1 ${styles.roleDescription}`}>{role.description}</small>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {formError && <div className="alert alert-danger py-2">{formError}</div>}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Inviting...' : 'Invite User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
